import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Crown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchBillingMe, fmtLKR } from "@/lib/billing";
import { useAuth } from "@/lib/auth";

const BillingSuccessPage = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let timer;
    const poll = async () => {
      try {
        const m = await fetchBillingMe();
        if (!mounted) return;
        setMe(m);
        const o = (m.orders || []).find((x) => x.order_id === orderId);
        setOrder(o || null);
        if (o?.status === "success") {
          await refresh();
          setLoading(false);
          return;
        }
      } catch {}
      timer = setTimeout(poll, 3000);
    };
    poll();
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="billing-success-page">
      <Card className="max-w-lg w-full text-center border-emerald-500/40">
        <CardHeader>
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <CardTitle>Payment {order?.status === "success" ? "successful" : "received"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {order?.status === "success" ? (
            <>
              <p className="text-sm text-muted-foreground">
                Your <b>{order.plan_id?.replace("_", " ")}</b> subscription is active. {fmtLKR(order.amount)} · {orderId}
              </p>
              <div className="flex gap-2 justify-center pt-2">
                <Button asChild data-testid="billing-success-dashboard">
                  <Link to="/dashboard"><Sparkles className="h-4 w-4" /> Open dashboard</Link>
                </Button>
                <Button asChild variant="outline" data-testid="billing-success-profile">
                  <Link to="/profile"><Crown className="h-4 w-4" /> View subscription</Link>
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                We're confirming your payment with PayHere. This usually takes a few seconds.
              </p>
              {orderId && (
                <p className="text-xs text-muted-foreground">Order: {orderId}</p>
              )}
              <Button asChild variant="outline" data-testid="billing-success-pricing">
                <Link to="/pricing">Back to pricing</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSuccessPage;
